import { Heading, Inline, Stack, Text } from '@etg/wings';
import { useDirection, useTranslation } from '@eti/providers';
import { Icon } from '@eti/components';
import { AgeType } from '@eti/schema-types';
import { css } from '@eti/styles';
import type {
  BoundType,
  TravelerSeatMapPricesType,
  TravelerSeatMapPriceType,
  TravelerType,
} from '../../../models';
import BidirectionalSeparatedElements from '../../../../../common/bidirectional-component/BidirectionalSeparatedElements';

const iconStyles = (isRTL: boolean) => css`
  transform: ${isRTL ? 'scaleX(-1)' : 'scaleX(1)'};
`;

const seatStyles = css`
  margin-inline-start: 26px;
`;

interface PassengerSeatProps {
  bound: BoundType;
  passenger: TravelerType;
  passengerBoundSeats: TravelerSeatMapPricesType;
}

const PassengerBoundSeat = ({ bound, passenger, passengerBoundSeats }: PassengerSeatProps) => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();

  const getSeatText = (passengerAge: string, seatInSegment?: TravelerSeatMapPriceType) => {
    if (passengerAge === AgeType.Inf) {
      return t('OrderDetails.WhatIsIncluded.Included.Seating.Infant.Text');
    }

    if (!seatInSegment) {
      return '-';
    }
    return seatInSegment.seat;
  };

  return (
    <Stack data-testid="passenger-seat" spacing={4}>
      <Inline spacing={8}>
        <Icon
          className={iconStyles(isRTL)}
          data-testid="passenger-info-seat-icon"
          id="seat-thin"
          size="20px"
        />
        <Heading data-testid="passenger-info-seat-title" level={4}>
          {t('OrderDetails.Passenger.Drawer.Seat.Title')}
        </Heading>
      </Inline>
      <Stack role="list" spacing={4}>
        {bound.segments.map((segment, segmentIndex) => {
          if (segment?.__typename === 'TripSegment') {
            const tripSegmentIndex = segmentIndex / 2;

            const segmentSeat = getSeatText(
              passenger.ageType,
              passengerBoundSeats.find(
                (passengerSeat) => passengerSeat?.segmentIndex === tripSegmentIndex,
              ),
            );

            return (
              <Inline
                className={seatStyles}
                key={`${bound.boundId}-${segment.segmentId}-seat`}
                role="listitem"
                spacing={0}
              >
                <BidirectionalSeparatedElements
                  data-testid={`passenger-seat-${bound.boundId}-${segment.segmentId}-segment-origin-destination`}
                  elements={[segment.origin.cityName, segment.destination.cityName]}
                />
                <Text
                  data-testid={`passenger-seat-${bound.boundId}-${segment.segmentId}-segment-seat`}
                >
                  : {segmentSeat}
                </Text>
              </Inline>
            );
          }
          return null;
        })}
      </Stack>
    </Stack>
  );
};

export default PassengerBoundSeat;
